import { useSelector } from 'react-redux'
import { useEffect, useState } from 'react'
import clsx from 'clsx'
import classes from './GenerateLinkModal.module.scss'
import { InputText } from 'primereact/inputtext'
import { Button } from 'primereact/button'
import { Tooltip, TooltipProps } from 'primereact/tooltip'
import ULModal from '../../../shared/ul-modal/ULModal'
import PmrE2EProjectModel from '../shared/interfaces/pmr-e2e-project-model'
import { PmrE2EProjectFullViewModel } from '../shared/interfaces/pmr-e2e-project-full-view-model'
import { ProjectFullViewRequestModel } from '../../../shared/models/ProjectFullViewRequestModel'
import { selectShowHideColumnProjects } from '../../../features/projectManagementPmr/projectManagementPmrSlice'
import ProjectService from '../../../services/ProjectService'

export interface GenerateLinkSelectedProject {
  flexProjectNumber: string
  orderLineDescription: string
  e2EProjectId: string
  e2EProjectName: string
}

interface GenerateLinkModalProps {
  visible: boolean
  onHide: () => void
  data?: GenerateLinkSelectedProject | null
  project: PmrE2EProjectModel
}

const tooltipText = 'Link Copied!'
const tooltipDefaultProps: TooltipProps = {
  mouseTrackTop: 30,
  mouseTrackLeft: 5,
  mouseTrack: true,
}

export default function GenerateLinkModal({
  data,
  onHide,
  visible,
  project,
}: GenerateLinkModalProps) {
  const selectShowHideColumnsForFullView = useSelector(selectShowHideColumnProjects)

  const forceEncodeURIComponent = (str: string) => {
    if (str.includes('%')) str = str.replaceAll('%', '%25') // Encode % first to avoid double encoding when it comes before other special characters

    return encodeURIComponent(str).replace(/[()&$#]/g, c => {
      return '%' + c.charCodeAt(0).toString(16).toUpperCase()
    })
  }

  const encodedLineDescription = forceEncodeURIComponent(data?.orderLineDescription ?? '')
  const encodedProjectName = forceEncodeURIComponent(data?.e2EProjectName ?? '')
  const generatedFullViewKey = Math.random().toString(36).slice(2)
  const fpnURL = `${process.env.REACT_APP_GMAWB_ALL_PROJECTS_URL}?bypass=true&flexProjectNumber=${data?.flexProjectNumber}`
  const fonURL = `${process.env.REACT_APP_GMAWB_ALL_PROJECTS_URL}?bypass=true&orderLineDescription=${encodedLineDescription}&e2EProjectId=${data?.e2EProjectId}&e2EProjectName=${encodedProjectName}`
  const fpURL = `${process.env.REACT_APP_GMAWB_PROJECT_URL}/${encodedProjectName}?fullViewProjectKey=${generatedFullViewKey}`

  const [copiedText, setCopiedText] = useState({
    fpn: false,
    fon: false,
    fp: false,
  })

  const copyToClipboard = (value: string) => {
    navigator.clipboard.writeText(value)
  }

  const onCopyGenerateLink = async () => {
    const fullViewProps: PmrE2EProjectFullViewModel = {
      project: project,
      showFlexTasks: {
        isOn: true,
        isFromClicked: false,
      },
      showHideColumn: selectShowHideColumnsForFullView,
      refiners: {
        projects: {
          projectNames: [data?.e2EProjectId],
        },
      },
    }
    const requestPayload: ProjectFullViewRequestModel = {
      key: generatedFullViewKey,
      keyCacheValueDetail: JSON.stringify(fullViewProps),
    }
    await ProjectService.setProjectFullViewCacheKey(requestPayload)
  }

  const _onHide = () => {
    if (onHide) onHide()
    setCopiedText({
      fpn: false,
      fon: false,
      fp: false,
    })
  }

  useEffect(() => {
    if (visible) {
      onCopyGenerateLink()
    }
  }, [visible])

  return (
    <ULModal
      visible={visible}
      onHide={_onHide}
      header="Share Link"
      footer={
        <div
          className={clsx(
            classes['d-flex'],
            classes['d-flex-column'],
            classes['d-flex-align-items-center']
          )}
        >
          <Button className="p-button-secondary" onClick={_onHide}>
            CLOSE
          </Button>
        </div>
      }
    >
      <div className={clsx(classes['d-flex'], classes['d-flex-column'])}>
        <div className={clsx(classes['d-flex'], classes['d-flex-column'])}>
          <label className="font-weight-bold">Link to FLEX Project Number</label>
          <div className={clsx(classes['d-flex'], classes['d-flex-row'])}>
            <InputText value={fpnURL} readOnly width={'350px'} />

            <Tooltip
              {...tooltipDefaultProps}
              target="#fpn-button"
              className={clsx(classes['custom-tooltip'], copiedText.fpn && classes['active'])}
            />
            <Button
              id="fpn-button"
              onClick={event => {
                copyToClipboard(fpnURL)
                setCopiedText(prev => ({ ...prev, fpn: true }))
              }}
              data-pr-tooltip={tooltipText}
            >
              COPY LINK
            </Button>
          </div>
        </div>
        <div className={clsx(classes['d-flex'], classes['d-flex-column'])}>
          <label className="font-weight-bold">Link to selected Order Line</label>
          <div className={clsx(classes['d-flex'], classes['d-flex-row'])}>
            <InputText value={fonURL} readOnly width={'350px'} />
            <Tooltip
              {...tooltipDefaultProps}
              target="#fon-button"
              className={clsx(classes['custom-tooltip'], copiedText.fon && classes['active'])}
            />
            <Button
              id="fon-button"
              onClick={() => {
                copyToClipboard(fonURL)
                setCopiedText(prev => ({ ...prev, fon: true }))
              }}
              data-pr-tooltip={tooltipText}
            >
              COPY LINK
            </Button>
          </div>
        </div>

        <div className={clsx(classes['d-flex'], classes['d-flex-column'])}>
          <label className="font-weight-bold">Link to GMAWB Project</label>
          <div className={clsx(classes['d-flex'], classes['d-flex-row'])}>
            <InputText value={fpURL} readOnly width={'350px'} />
            <Tooltip
              {...tooltipDefaultProps}
              target="#fp-button"
              className={clsx(classes['custom-tooltip'], copiedText.fp && classes['active'])}
            />
            <Button
              id="fp-button"
              onClick={() => {
                copyToClipboard(fpURL)
                setCopiedText(prev => ({ ...prev, fp: true }))
              }}
              data-pr-tooltip={tooltipText}
            >
              COPY LINK
            </Button>
          </div>
        </div>
      </div>
    </ULModal>
  )
}
